import {
  SecurityChallengeMetadata,
  SecurityChallengePurpose,
  SecurityChallengeService,
} from '@/services/securityChallenge.service';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

export const SECURITY_CHALLENGE_OTP_LEN = 6;
const RESEND_COOLDOWN_SEC = 60;

export function isOnboardingStepFinished(step?: string | null): boolean {
  return (step ?? '').toLowerCase() === 'finished';
}

type ApiErrorData = {
  message?: string;
  errors?: Record<string, string[] | string>;
};

function getApiErrorData(error: unknown): ApiErrorData | null {
  if (!error || typeof error !== 'object') return null;

  const axiosError = error as AxiosError<ApiErrorData>;
  if (
    axiosError.response?.data &&
    typeof axiosError.response.data === 'object'
  ) {
    return axiosError.response.data;
  }

  if ('errors' in error || 'message' in error) {
    return error as ApiErrorData;
  }

  return null;
}

export function formatApiFieldError(message: string[] | string): string {
  return Array.isArray(message) ? String(message[0]) : String(message);
}

/**
 * Extrai a "chave" de erro retornada pela API de challenge, priorizando o
 * objeto `errors` (por campo) e caindo para `message`.
 */
export function extractChallengeErrorKey(error: unknown): string | null {
  const data = getApiErrorData(error);
  if (!data) return null;

  if (data.errors) {
    const firstField = Object.values(data.errors)[0];
    const value = Array.isArray(firstField) ? firstField[0] : firstField;
    if (value) return String(value);
  }

  return data.message ?? null;
}

/**
 * Indica se o erro de uma ação protegida foi causado por problema no
 * challenge_token (ausente, expirado, já usado ou metadata divergente),
 * sinalizando que o modal de 2FA deve ser reaberto.
 */
export function isChallengeTokenError(error: unknown): boolean {
  const data = getApiErrorData(error);
  if (!data) return false;

  if (data.errors) {
    const hasChallengeTokenField = Object.keys(data.errors).some(
      (key) => key.toLowerCase().replace(/-/g, '_') === 'challenge_token',
    );
    if (hasChallengeTokenField) return true;
  }

  const text = `${data.message ?? ''}`.toLowerCase();
  if (text.includes('challenge') && text.includes('token')) return true;

  const key = extractChallengeErrorKey(error) ?? '';
  return (
    key.toLowerCase().includes('challenge') &&
    (key.toLowerCase().includes('token') ||
      key.toLowerCase().includes('expired') ||
      key.toLowerCase().includes('mismatch') ||
      key.toLowerCase().includes('obrigat') ||
      key.toLowerCase().includes('required'))
  );
}

interface UseSecurityChallengeParams {
  purpose: SecurityChallengePurpose;
  metadata?: SecurityChallengeMetadata;
  onVerified?: (challengeToken: string) => void;
}

export function useSecurityChallenge({
  purpose,
  metadata,
  onVerified,
}: UseSecurityChallengeParams) {
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [resendCooldownSec, setResendCooldownSec] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  const onVerifiedRef = useRef(onVerified);
  onVerifiedRef.current = onVerified;

  useEffect(() => {
    if (resendCooldownSec <= 0) return;
    const id = window.setTimeout(
      () => setResendCooldownSec((s) => Math.max(0, s - 1)),
      1000,
    );
    return () => window.clearTimeout(id);
  }, [resendCooldownSec]);

  const { mutateAsync: sendMutate, isPending: isSending } = useMutation({
    mutationFn: () =>
      SecurityChallengeService.sendChallenge(purpose, metadataRef.current),
  });

  const { mutateAsync: verifyMutate, isPending: isVerifying } = useMutation({
    mutationFn: (code: string) => {
      if (!challengeId) {
        return Promise.reject(new Error('missing_challenge_id'));
      }
      return SecurityChallengeService.verifyChallenge(challengeId, code);
    },
  });

  const sendCode = useCallback(async () => {
    setError(null);
    try {
      const res = await sendMutate();
      setChallengeId(res.challenge_id);
      setExpiresAt(Date.now() + res.expires_in * 1000);
      setResendCooldownSec(RESEND_COOLDOWN_SEC);
      return res;
    } catch (err) {
      setError(extractChallengeErrorKey(err));
      throw err;
    }
  }, [sendMutate]);

  const verifyCode = useCallback(
    async (code: string) => {
      setError(null);
      try {
        const res = await verifyMutate(code);
        const token = res?.challenge_token;
        if (!token) {
          throw new Error('missing_challenge_token');
        }
        onVerifiedRef.current?.(token);
        return res;
      } catch (err) {
        setError(extractChallengeErrorKey(err));
        throw err;
      }
    },
    [verifyMutate],
  );

  const reset = useCallback(() => {
    setChallengeId(null);
    setExpiresAt(null);
    setResendCooldownSec(0);
    setError(null);
  }, []);

  return {
    sendCode,
    verifyCode,
    reset,
    challengeId,
    expiresAt,
    resendCooldownSec,
    isSending,
    isVerifying,
    isLoading: isSending || isVerifying,
    error,
  };
}
